/*
 * Эффекты нажатия: тонкая ударная волна, короткая вспышка и сканирующие линии.
 * Только transform и opacity (композитинг без перерисовки), слой не перехватывает клики.
 * Если у кнопки есть предок [data-fx-host], эффект обрезается его границами.
 */
const reduced = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const EASE_OUT = "cubic-bezier(.12,.9,.2,1)";
const RING = "radial-gradient(circle, transparent 0 60%, rgba(255,210,168,.95) 61.5%, rgba(255,155,87,.5) 63%, transparent 66%)";

type Area = { root: HTMLDivElement; x: number; y: number; w: number; h: number; far: number };

function area(from: Element | null, cx?: number, cy?: number): Area {
  const host = from?.closest<HTMLElement>("[data-fx-host]") || null;
  const root = document.createElement("div");
  root.setAttribute("aria-hidden", "true");
  let w = window.innerWidth;
  let h = window.innerHeight;
  let x = cx ?? w / 2;
  let y = cy ?? h / 2;
  if (host) {
    const r = host.getBoundingClientRect();
    const b = from ? from.getBoundingClientRect() : r;
    w = r.width;
    h = r.height;
    x = b.left + b.width / 2 - r.left;
    y = b.top + b.height / 2 - r.top;
    Object.assign(root.style, { position: "absolute", inset: "0", pointerEvents: "none", overflow: "hidden", borderRadius: "inherit", zIndex: "5" });
    host.appendChild(root);
  } else {
    Object.assign(root.style, { position: "fixed", inset: "0", pointerEvents: "none", overflow: "hidden", zIndex: "2500" });
    document.body.appendChild(root);
  }
  const far = Math.hypot(Math.max(x, w - x), Math.max(y, h - y));
  return { root, x, y, w, h, far };
}

/** Убираем слой, когда закончилась самая долгая анимация (а не по таймеру). */
function cleanup(a: Area) {
  const anims = a.root.getAnimations({ subtree: true });
  Promise.all(anims.map((x) => x.finished.catch(() => null))).then(() => a.root.remove());
  window.setTimeout(() => a.root.remove(), 3000);
}

function box(a: Area, css: Partial<CSSStyleDeclaration>) {
  const el = document.createElement("div");
  Object.assign(el.style, { position: "absolute", willChange: "transform, opacity", opacity: "0", ...css });
  a.root.appendChild(el);
  return el;
}

function ring(a: Area, delay: number, duration: number, peak: number, reverse = false) {
  const size = a.far * 2;
  const el = box(a, { left: `${a.x - size / 2}px`, top: `${a.y - size / 2}px`, width: `${size}px`, height: `${size}px`, borderRadius: "50%", background: RING });
  const frames = reverse
    ? [{ transform: "scale(1)", opacity: 0 }, { transform: "scale(.6)", opacity: peak, offset: 0.3 }, { transform: "scale(0)", opacity: 0 }]
    : [{ transform: "scale(0)", opacity: peak }, { transform: "scale(1)", opacity: 0 }];
  el.animate(frames, { duration, delay, easing: reverse ? "cubic-bezier(.6,0,.9,.4)" : EASE_OUT, fill: "both" });
}

function lines(a: Area, duration: number, reverse = false) {
  const mk = (toY: number) => {
    const el = box(a, { left: "0", top: `${a.y - 1}px`, width: "100%", height: "2px", background: "linear-gradient(90deg, transparent, rgba(255,210,168,.95) 20%, rgba(255,241,224,1) 50%, rgba(255,210,168,.95) 80%, transparent)" });
    const frames = reverse
      ? [{ transform: `translateY(${toY}px)`, opacity: 0.8 }, { transform: "translateY(0)", opacity: 0 }]
      : [{ transform: "translateY(0)", opacity: 0.95 }, { transform: `translateY(${toY}px)`, opacity: 0 }];
    el.animate(frames, { duration, easing: reverse ? "cubic-bezier(.6,0,.9,.4)" : EASE_OUT, fill: "both" });
  };
  mk(-a.y);
  mk(a.h - a.y);
}

function flash(a: Area, duration: number, peak: number) {
  const el = box(a, { inset: "0", background: "rgba(255,155,87,1)" });
  el.animate([{ opacity: 0 }, { opacity: peak, offset: 0.18 }, { opacity: 0 }], { duration, easing: "ease-out", fill: "both" });
}

/** Старт: волна расходится от кнопки, линии разъезжаются к краям окна. */
export function burstFrom(button: Element) {
  if (reduced() || typeof document === "undefined") return;
  const a = area(button);
  ring(a, 0, 800, 1);
  ring(a, 110, 900, 0.55);
  lines(a, 620);
  flash(a, 420, 0.16);
  cleanup(a);
}

/** Для событий без кнопки (найден матч, сервер готов): та же волна на весь экран. */
export function burstAt(x: number, y: number) {
  if (reduced() || typeof document === "undefined") return;
  const a = area(null, x, y);
  ring(a, 0, 900, 1);
  ring(a, 120, 1000, 0.5);
  lines(a, 700);
  flash(a, 450, 0.12);
  cleanup(a);
}

/** Отмена: волна и линии стягиваются обратно в кнопку. */
export function collapseTo(button: Element) {
  if (reduced() || typeof document === "undefined") return;
  const a = area(button);
  ring(a, 0, 520, 0.9, true);
  lines(a, 480, true);
  cleanup(a);
}
